import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { usePrefs, useLabels } from 'wsm-common-data';
import { useSelector } from 'react-redux';
import useGalleryModal from '../hooks/useGalleryModal';
import usePubSubListeners from '../hooks/usePubSubListeners';
import useOpenGalleryParam from '../hooks/useOpenGalleryParam';
import useImageTitle from '../hooks/useImageTitle';
import useMedia from '../hooks/useMedia';

const GalleryContext = createContext(null);
GalleryContext.displayName = 'GalleryContext';

export const useGalleryContext = () => {
	const context = useContext(GalleryContext);
	if (!context) {
		throw new Error(
			'useGalleryContext must be used within a GalleryProvider'
		);
	}
	return context;
};

export const GalleryProvider = ({ children, overrides }) => {
	const {
		openModal,
		setOpenModal,
		carouselIndex,
		setCarouselIndex,
		activeTab,
		setActiveTab,
		primarySliderRef,
		onOpenModal,
		onClose
	} = useGalleryModal();

	const prefs = usePrefs();
	const labels = useLabels();
	const isDesktop = useMedia('(min-width: 576px)', true);

	const galleryToolbarPlacement = prefs.galleryToolbarPlacement || 'top';
	const isBlackBackgroundModal = prefs.enableBlackBackgroundModal === 'true';
	const toolbarIconStyle =
		prefs.toolbarIconStyle === 'round' ? 'round' : 'default';
	const toolbarIconColor =
		prefs.toolbarIconColor === 'light' ? 'light' : 'dark';

	const { imagesToDisplay = [] } = useSelector(
		(storeState) => storeState.media
	);
	const {
		vehicle = {},
		pathParameters,
		accountId
	} = useSelector((storeState) => storeState.widgetInfo);

	const imgTitle = useImageTitle({ vehicle, labels });

	let totalImages = imagesToDisplay.length;
	if (
		totalImages === 1 &&
		imagesToDisplay[0]?.src?.includes('unavailable_stockphoto')
	) {
		totalImages = 0;
	}

	const firstThumbnail =
		totalImages > 0 ? imagesToDisplay[0]?.thumbnail : null;

	usePubSubListeners({
		onClose,
		openModal,
		totalImages,
		firstThumbnail,
		isBlackBackgroundModal
	});

	useOpenGalleryParam({ onOpenModal, setCarouselIndex, setActiveTab });

	const state = {
		openModal,
		carouselIndex,
		activeTab,
		totalImages,
		imagesToDisplay
	};

	const actions = {
		setOpenModal,
		setCarouselIndex,
		setActiveTab,
		onOpenModal,
		onClose
	};

	const meta = {
		primarySliderRef: overrides?.primarySliderRef || primarySliderRef,
		isDesktop,
		galleryToolbarPlacement,
		imgTitle,
		vehicle,
		prefs,
		labels,
		isBlackBackgroundModal,
		toolbarIconStyle,
		toolbarIconColor,
		pathParameters,
		accountId,
		...overrides
	};

	const value = useMemo(
		() => ({ state, actions, meta }),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			openModal,
			carouselIndex,
			activeTab,
			totalImages,
			isDesktop,
			galleryToolbarPlacement,
			imgTitle,
			toolbarIconStyle,
			toolbarIconColor
		]
	);

	return (
		<GalleryContext.Provider value={value}>
			{children}
		</GalleryContext.Provider>
	);
};

GalleryProvider.propTypes = {
	children: PropTypes.node.isRequired,
	overrides: PropTypes.shape({
		primarySliderRef: PropTypes.shape({})
	})
};

GalleryProvider.defaultProps = {
	overrides: null
};

export default GalleryContext;
